import React, { useEffect, useState } from 'react';
import axios from '../lib/axios';
import { AlertTriangle, Plus, FileText, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const Incidents = () => {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newIncident, setNewIncident] = useState({
    child_id: '',
    datetime: '',
    location: '',
    incident_type: 'incident',
    description: '',
    body_part: '',
    witnesses: [],
    staff_involved: [],
    first_aid_given: '',
    medical_care_sought: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [incidentsRes, childrenRes] = await Promise.all([
        axios.get('/incident-reports'),
        axios.get('/children'),
      ]);
      
      setIncidents(incidentsRes.data.sort((a, b) => new Date(b.datetime) - new Date(a.datetime)));
      setChildren(childrenRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load incidents');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIncident = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/incident-reports', {
        ...newIncident,
        datetime: new Date(newIncident.datetime).toISOString(),
      });
      toast.success('Incident report created successfully!');
      setIsAddDialogOpen(false);
      setNewIncident({
        child_id: '',
        datetime: '',
        location: '',
        incident_type: 'incident',
        description: '',
        body_part: '',
        witnesses: [],
        staff_involved: [],
        first_aid_given: '',
        medical_care_sought: false,
      });
      fetchData();
    } catch (error) {
      console.error('Error creating incident:', error);
      toast.error(error.response?.data?.detail || 'Failed to create incident report');
    }
  };

  const getChildName = (childId) => {
    const child = children.find(c => c.id === childId);
    return child?.name || 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="incidents-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Incident Reports
          </h1>
          <p className="text-slate-600 mt-1">Track and manage incident, injury, and illness reports</p>
        </div>
        {user?.role !== 'parent' && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="create-incident-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Incident Report</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddIncident} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="child">Child</Label>
                  <Select
                    value={newIncident.child_id}
                    onValueChange={(value) => setNewIncident({ ...newIncident, child_id: value })}
                    required
                  >
                    <SelectTrigger className="mt-1.5" data-testid="incident-child-select">
                      <SelectValue placeholder="Select child" />
                    </SelectTrigger>
                    <SelectContent>
                      {children.map((child) => (
                        <SelectItem key={child.id} value={child.id}>
                          {child.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="datetime">Date & Time</Label>
                    <Input
                      id="datetime"
                      type="datetime-local"
                      value={newIncident.datetime}
                      onChange={(e) => setNewIncident({ ...newIncident, datetime: e.target.value })}
                      required
                      className="mt-1.5"
                      data-testid="incident-datetime-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="type">Type</Label>
                    <Select
                      value={newIncident.incident_type}
                      onValueChange={(value) => setNewIncident({ ...newIncident, incident_type: value })}
                    >
                      <SelectTrigger className="mt-1.5" data-testid="incident-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="injury">Injury</SelectItem>
                        <SelectItem value="illness">Illness</SelectItem>
                        <SelectItem value="incident">Incident</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={newIncident.location}
                    onChange={(e) => setNewIncident({ ...newIncident, location: e.target.value })}
                    placeholder="e.g., Playground, Classroom A"
                    required
                    className="mt-1.5"
                    data-testid="incident-location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newIncident.description}
                    onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                    placeholder="Detailed description of what happened..."
                    required
                    rows={4}
                    className="mt-1.5"
                    data-testid="incident-description-input"
                  />
                </div>
                <div>
                  <Label htmlFor="body_part">Body Part (if injury)</Label>
                  <Input
                    id="body_part"
                    value={newIncident.body_part}
                    onChange={(e) => setNewIncident({ ...newIncident, body_part: e.target.value })}
                    placeholder="e.g., Left knee, Right arm"
                    className="mt-1.5"
                    data-testid="incident-body-part-input"
                  />
                </div>
                <div>
                  <Label htmlFor="first_aid">First Aid Given</Label>
                  <Textarea
                    id="first_aid"
                    value={newIncident.first_aid_given}
                    onChange={(e) => setNewIncident({ ...newIncident, first_aid_given: e.target.value })}
                    placeholder="Describe first aid or response provided..."
                    rows={2}
                    className="mt-1.5"
                    data-testid="incident-first-aid-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" className="flex-1" data-testid="submit-incident-button">
                    Create Report
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsAddDialogOpen(false)}
                    data-testid="cancel-incident-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Incidents List */}
      <div className="space-y-3">
        {incidents.map((incident) => (
          <div
            key={incident.id}
            className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm hover:shadow-md transition-shadow"
            data-testid={`incident-card-${incident.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  incident.incident_type === 'injury' ? 'bg-rose-100' :
                  incident.incident_type === 'illness' ? 'bg-amber-100' :
                  'bg-slate-100'
                }`}>
                  <AlertTriangle className={`w-5 h-5 ${
                    incident.incident_type === 'injury' ? 'text-rose-600' :
                    incident.incident_type === 'illness' ? 'text-amber-600' :
                    'text-slate-600'
                  }`} />
                </div>
                <div>
                  <h3 className="font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {getChildName(incident.child_id)}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-slate-600 mt-1">
                    <Clock className="w-3.5 h-3.5" />
                    <span className="timestamp">{new Date(incident.datetime).toLocaleString()}</span>
                    <span className="text-slate-400">•</span>
                    <span>{incident.location}</span>
                  </div>
                </div>
              </div>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                incident.incident_type === 'injury' ? 'bg-rose-100 text-rose-800' :
                incident.incident_type === 'illness' ? 'bg-amber-100 text-amber-800' :
                'bg-slate-100 text-slate-800'
              }`}>
                {incident.incident_type}
              </span>
            </div>
            <p className="text-slate-700 mb-3">{incident.description}</p>
            {incident.body_part && (
              <p className="text-sm text-slate-600 mb-2">
                <span className="font-medium">Body Part:</span> {incident.body_part}
              </p>
            )}
            {incident.first_aid_given && (
              <p className="text-sm text-slate-600 mb-2">
                <span className="font-medium">First Aid:</span> {incident.first_aid_given}
              </p>
            )}
            <div className="flex items-center gap-2 mt-4 pt-4 border-t border-slate-100">
              {incident.parent_signature ? (
                <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Parent Acknowledged
                </span>
              ) : (
                <span className="text-sm text-amber-600 font-medium flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Pending Parent Acknowledgment
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {incidents.length === 0 && (
        <div className="text-center py-12">
          <AlertTriangle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No incidents reported</h3>
          <p className="text-slate-600 mb-4">
            All incident reports will appear here
          </p>
        </div>
      )}
    </div>
  );
};

export default Incidents;
